/**
 * Video Upload Component
 * Allows uploading drill videos with validation
 */
import { Upload, Video, X, CheckCircle } from 'lucide-react'
import PropTypes from 'prop-types'
import React, { useState, useRef } from 'react'

import { Button } from '@/components/elements/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/elements/card'
import { cn } from '@/utils/helpers'

const MAX_VIDEO_SIZE = 500 * 1024 * 1024 // 500MB
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm']

export function VideoUpload({ onVideoSelect, value = null }) {
  const [selectedVideo, setSelectedVideo] = useState(value)
  const [error, setError] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef(null)

  const validateVideo = (file) => {
    if (!file) return 'No file selected'

    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload MP4, MOV, AVI, or WEBM'
    }

    if (file.size > MAX_VIDEO_SIZE) {
      return `File too large. Maximum size is ${MAX_VIDEO_SIZE / (1024 * 1024)}MB`
    }

    return null
  }

  const handleFileSelect = (file) => {
    const validationError = validateVideo(file)
    if (validationError) {
      setError(validationError)
      setSelectedVideo(null)
      onVideoSelect?.(null)
      return
    }

    setError(null)
    setSelectedVideo(file)
    onVideoSelect?.(file)
  }

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleRemove = () => {
    setSelectedVideo(null)
    setError(null)
    onVideoSelect?.(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Drill Video (Optional)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!selectedVideo ? (
          <div
            role="button"
            tabIndex={0}
            className={cn(
              'cursor-pointer rounded-lg border-[1.5px] border-dashed p-8 text-center transition-colors',
              isDragging ? 'border-primary bg-primary/5' : 'border-input hover:border-ring',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                fileInputRef.current?.click()
              }
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_VIDEO_TYPES.join(',')}
              onChange={handleFileInputChange}
              className="hidden"
            />

            <div className="flex flex-col items-center gap-3">
              <div className="rounded-full bg-gray-100 p-4">
                <Upload className="h-8 w-8 text-gray-600" />
              </div>

              <div className="space-y-1">
                <p className="text-sm font-medium">Drop video here or click to browse</p>
                <p className="text-muted-foreground text-xs">
                  MP4, MOV, AVI, WEBM up to {MAX_VIDEO_SIZE / (1024 * 1024)}MB
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-green-100 p-2">
                <Video className="h-5 w-5 text-green-700" />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-green-900">
                      {selectedVideo.name}
                    </p>
                    <p className="mt-1 text-xs text-green-700">
                      {formatFileSize(selectedVideo.size)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 flex-shrink-0 text-green-600" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemove}
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="text-destructive bg-destructive/10 rounded-lg p-3 text-sm">{error}</div>
        )}

        <p className="text-muted-foreground text-xs">
          Video upload will be queued if offline and automatically synced when connection is
          restored
        </p>
      </CardContent>
    </Card>
  )
}

VideoUpload.propTypes = {
  onVideoSelect: PropTypes.func.isRequired,
  value: PropTypes.instanceOf(File),
}
